import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';

const FormWrapper = styled.div`
  background-color: ${props => props.theme.colors.lightColor};
  padding: 30px;
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadows.regular};
`;

const FormTitle = styled.h3`
  margin-bottom: 20px;
  text-align: center;
  color: ${props => props.theme.colors.secondaryColor};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.grey[300]};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.main};
  
  &:focus {
    border-color: ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.primaryColor};
    outline: none;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.grey[300]};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.main};
  resize: vertical;
  min-height: 120px;
  
  &:focus {
    border-color: ${props => props.error ? props.theme.colors.dangerColor : props.theme.colors.primaryColor};
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.dangerColor};
  font-size: ${props => props.theme.fontSizes.small};
  margin-top: 5px;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.successColor};
  text-align: center;
  padding: 10px;
  margin-bottom: 20px;
  background-color: rgba(40, 167, 69, 0.1);
  border-radius: 4px;
`;

const QuickInquiryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[0-9]{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone number is invalid';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // This would be an actual API call in a real application
      // For demo purposes, we'll simulate an API call with a timeout
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setSubmitSuccess(true);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({
        ...errors,
        submit: 'Failed to submit form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <FormWrapper>
      <FormTitle>Quick Inquiry</FormTitle>
      
      {submitSuccess && (
        <SuccessMessage>Thank you for your message! We'll get back to you soon.</SuccessMessage>
      )}
      
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <FormInput
            type="text"
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            required
          />
          {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <FormInput
            type="email"
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <FormInput
            type="tel"
            name="phone"
            placeholder="Your Phone"
            value={formData.phone}
            onChange={handleChange}
            error={errors.phone}
            required
          />
          {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
        </FormGroup>
        
        <FormGroup>
          <FormTextarea
            name="message"
            placeholder="Your Message"
            value={formData.message}
            onChange={handleChange}
            error={errors.message}
            required
          />
          {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
        </FormGroup>
        
        {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
        
        <Button 
          type="submit" 
          variant="primary" 
          fullWidth 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </form>
    </FormWrapper>
  );
};

export default QuickInquiryForm;
